import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import {
  cadastrarCodigos,
  cadastroVarejoKeys,
  deletarCodigo,
  fetchCodigosCadastrados,
  fetchProdutos,
} from '@/services/cadastroVarejoService';
import type {
  ICadastroVarejoApiError,
  ICadastroVarejoSessaoItem,
  IProdutoOption,
} from '@/types/cadastroVarejo';
import './varejo.css';

export const Route = createFileRoute('/_authenticated/configuracao/varejo/')({
  component: CadastroVarejoPage,
});

const CLASS_VENDA_PADRAO = 'Varejo';

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError<ICadastroVarejoApiError>(error)) {
    return error.response?.data?.error ?? fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

function CadastroVarejoPage() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [registrationList, setRegistrationList] = useState<ICadastroVarejoSessaoItem[]>([]);
  const [message, setMessage] = useState('');

  const {
    data: productOptions = [],
    isError: isProdutosError,
    error: produtosError,
  } = useQuery({
    queryKey: cadastroVarejoKeys.produtos(),
    queryFn: fetchProdutos,
  });

  const {
    data: registeredCodes = [],
    isError: isCodigosError,
    error: codigosError,
  } = useQuery({
    queryKey: cadastroVarejoKeys.codigos(),
    queryFn: fetchCodigosCadastrados,
  });

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return productOptions;
    return productOptions.filter(
      (prod) =>
        prod.referencia.toLowerCase().includes(term) ||
        prod.nome.toLowerCase().includes(term),
    );
  }, [productOptions, searchTerm]);

  const cadastrarMutation = useMutation({
    mutationFn: cadastrarCodigos,
    onSuccess: () => {
      setMessage('Códigos cadastrados com sucesso!');
      setRegistrationList([]);
      void queryClient.invalidateQueries({ queryKey: cadastroVarejoKeys.codigos() });
    },
    onError: (error) => {
      setMessage(`Erro ao cadastrar códigos: ${extractApiError(error, 'Erro desconhecido.')}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletarCodigo,
    onSuccess: () => {
      setMessage('Código deletado com sucesso!');
      void queryClient.invalidateQueries({ queryKey: cadastroVarejoKeys.codigos() });
    },
    onError: (error) => {
      setMessage(`Erro ao deletar código: ${extractApiError(error, 'Erro desconhecido.')}`);
    },
  });

  const handleProductSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setDropdownVisible(true);
  };

  const handleProductSelect = (product: IProdutoOption) => {
    const registration: ICadastroVarejoSessaoItem = {
      codigo: product.referencia,
      descricao: product.nome,
      classVenda: CLASS_VENDA_PADRAO,
    };

    const exists = registrationList.some((item) => item.codigo === registration.codigo);
    if (!exists) {
      setRegistrationList((prev) => [...prev, registration]);
      setMessage(`Código "${registration.codigo}" adicionado com sucesso.`);
    } else {
      setMessage(`Código "${registration.codigo}" já está na lista.`);
    }
    setSearchTerm('');
    setDropdownVisible(false);
  };

  const handleRemoveRegistration = (index: number) => {
    setRegistrationList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitRegistration = () => {
    if (registrationList.length === 0) {
      setMessage('Nenhum código cadastrado para enviar.');
      return;
    }
    cadastrarMutation.mutate({ codigos: registrationList });
  };

  const handleDeleteCode = (id: string) => {
    deleteMutation.mutate(id);
  };

  const errorMessage = (() => {
    if (isProdutosError) {
      return `Erro ao buscar produtos: ${extractApiError(produtosError, 'Erro desconhecido.')}`;
    }
    if (isCodigosError) {
      return `Erro ao buscar códigos cadastrados: ${extractApiError(codigosError, 'Erro desconhecido.')}`;
    }
    return '';
  })();

  return (
    <div className="code-registration-container">
      <h2>Cadastrar Lista de Códigos</h2>
      {(message || errorMessage) && (
        <p className="message">{errorMessage || message}</p>
      )}

      <div className="form-group">
        <label htmlFor="productSearch">Pesquisar Código do Produto:</label>
        <input
          type="text"
          id="productSearch"
          value={searchTerm}
          onChange={handleProductSearch}
          placeholder="Digite para buscar o produto..."
        />
        {dropdownVisible && filteredOptions.length > 0 && (
          <div className="dropdown">
            {filteredOptions.map((product) => (
              <div
                key={product.id}
                className="dropdown-item"
                onClick={() => handleProductSelect(product)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleProductSelect(product);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {product.referencia} - {product.nome}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="registration-list">
        <h3>Códigos Cadastrados (Sessão)</h3>
        {registrationList.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Class Venda</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {registrationList.map((item, index) => (
                <tr key={`${item.codigo}-${index}`}>
                  <td>{item.codigo}</td>
                  <td>{item.descricao}</td>
                  <td>{item.classVenda}</td>
                  <td>
                    <button type="button" onClick={() => handleRemoveRegistration(index)}>
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Nenhum código cadastrado na sessão.</p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmitRegistration}
        disabled={registrationList.length === 0 || cadastrarMutation.isPending}
      >
        {cadastrarMutation.isPending ? 'Cadastrando...' : 'Cadastrar Códigos'}
      </button>

      <div className="registered-codes">
        <h3>Códigos Cadastrados no Banco</h3>
        {registeredCodes.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Código</th>
                <th>Descrição</th>
                <th>Class Venda</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {registeredCodes.map((code) => (
                <tr key={code.id}>
                  <td>{code.id}</td>
                  <td>{code.codigo}</td>
                  <td>{code.descricao}</td>
                  <td>{code.class_venda}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleDeleteCode(code.id)}
                      disabled={deleteMutation.isPending}
                    >
                      Deletar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Nenhum código cadastrado no banco.</p>
        )}
      </div>
    </div>
  );
}
